import { useMemo } from 'react';
import type { EnrollmentWithSubject } from '@repo/shared-types';
import { useAllEnrollments } from './useEnrollments';
import { useCan } from './useCan';
import { useServerNow } from './useServerNow';
import { useMonthlyReviewSummary } from './useProgress';
import { useInvoicingPreview } from './useInvoicing';
import { usePaymentsSummary } from './usePayments';
import { useReconcileStatus } from './useReconcileStatus';
import { currentMonth, prevMonth, reviewMonthFor } from '../lib/sessionTime';
import { chotNudgeState } from '../lib/chotNudge';
import { termNudgeState, type TermNudgeRow } from '../lib/termNudge';
import { buildAttentionItems, monthlyReviewItems, type AttentionItem } from '../lib/attentionItems';

function toRows(enrollments: EnrollmentWithSubject[]): TermNudgeRow[] {
  return enrollments.map((e) => ({
    enrollmentId: e.id,
    studentId: e.student.id,
    studentName: e.student.name,
    status: e.status,
    termState: e.termState,
  }));
}

/** Data for the workbench's "Cần chú ý" card (handoff 02). The original four
 *  billing signals stay behind the same manage:invoices gate as the banners
 *  they replaced; THI-105 added a fifth, the monthly-review nudge, which is
 *  NOT gated the same way (see below). The list itself comes from
 *  lib/attentionItems. Pulled out of AttentionCard so the P0.4 tab-bar dot
 *  can read `items.length` without rendering the card. */
export function useAttentionItems(): { canBill: boolean; items: AttentionItem[]; loading: boolean } {
  const canBill = useCan('manage:invoices');
  const now = useServerNow();
  const nowIso = now.toISOString();
  const month = useMemo(() => currentMonth(new Date(nowIso)), [nowIso]);
  const prev = useMemo(() => prevMonth(month), [month]);

  const preview = useInvoicingPreview(month, canBill);
  // Shared `['enrollments','all']` entry — see useAllEnrollments.
  const enrollments = useAllEnrollments(canBill);
  const summary = usePaymentsSummary(canBill);
  // Same TanStack key the layout also fetches (['bank-accounts',
  // 'reconcile-status']), so when both are enabled this reads a shared cache
  // entry rather than issuing a second request. Gated on canBill like the
  // other two sources above — a MEMBER whose workbench never renders this
  // card must not fire this fetch either (THI-136).
  const reconcile = useReconcileStatus(canBill);

  // THI-105/THI-189: the monthly-review nudge, deliberately NOT gated on
  // canBill — writing reviews is a MEMBER's daily job, unlike the four
  // signals above. Only live the 1st–24th (last month's reviews are the work
  // in progress; from the 25th the entry button in SlotDetailDialog covers it
  // instead).
  const reviewWindowOpen = now.getUTCDate() < 25;
  const reviewMonth = reviewMonthFor(now);
  // THI-189: one call, org-wide — replaces the cycle-1 `useSessions` guess at
  // "an active slot" (which could show the wrong subject, or none, if a
  // teacher had just taught a DIFFERENT slot last). The summary endpoint
  // already applies the exact roster rule the sweep does, so this can never
  // point at a subject that turns out to have nothing pending.
  const reviewSummary = useMonthlyReviewSummary(reviewMonth);
  const reviewItems = useMemo(
    () => (reviewWindowOpen && reviewSummary.data ? monthlyReviewItems(reviewSummary.data, reviewMonth) : []),
    [reviewWindowOpen, reviewSummary.data, reviewMonth],
  );

  const items = useMemo(
    () => [
      ...reviewItems,
      ...buildAttentionItems({
        chot: chotNudgeState(preview.data),
        month,
        prev,
        term: termNudgeState(toRows(enrollments.data ?? [])),
        unmatched: { count: summary.data?.unmatchedPaymentsCount ?? 0, total: summary.data?.unmatchedPaymentsTotal ?? 0 },
        reconcile: { exhausted: reconcile.data?.status === 'paused' },
      }),
    ],
    [reviewItems, preview.data, enrollments.data, summary.data, reconcile.data, month, prev],
  );

  // A clean workbench must not flash a card that then vanishes: only report
  // "loading" while none of the four billing sources has data yet AND at
  // least one is still pending — same rule as before THI-105, `canBill &&`
  // guarded so a disabled query's permanently-pending status (a MEMBER, or
  // canBill still resolving) can't hold this true forever.
  const billingNoData = !preview.data && !enrollments.data && !summary.data;
  const billingLoading = canBill && billingNoData && (preview.isPending || enrollments.isPending || summary.isPending);
  // Same shape for the review nudge: only "loading" while the window is open
  // AND we don't yet know whether there's anything to show — simpler now
  // that there is exactly one query instead of a two-stage fetch.
  const reviewLoading = reviewWindowOpen && reviewSummary.isPending;
  const loading = billingLoading || reviewLoading;

  return { canBill, items, loading };
}
